import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Grid, GridColumn, GridNoRecords } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Checkbox } from "@progress/kendo-react-inputs";
import { ExcelExport, ExcelExportColumn } from "@progress/kendo-react-excel-export";
import { toast } from "react-toastify";
import { Tile } from "components/UIComponents/Tile";
import {
  DateFormatCell,
  AmountFormatCell,
  DateFormat,
  AmountFormat,
  formatDateForAPI,
} from "components/common/formatter";
import { ToolbarHeaderCell } from "components/LedgerGrid/LedgerGridCells";
import {
  getMatterUndertakings,
  addUndertaking,
  updateUndertaking,
} from "api/index";
import ledgerStyles from "components/LedgerGrid/LedgerGrid.module.scss";
import { getLedgerExportFileName } from "components/LedgerGrid/ledgerExportFileName";
import { EditCommandCell } from "pages/Settings/GridComponents";
import { UndertakingForm } from "./UnderTakingForm";
import { GridLoader } from "components/GridLoader";
import ConfirmationDialog from "components/common/ConfirmationDialog";

const asUndertakingsList = (payload) => {
  const items = payload?.data ?? payload;
  return Array.isArray(items) ? items.filter((item) => item?.active !== false) : [];
};

const UndertakingsList = ({
  matterId,
  matterRef,
  clientId,
  refreshData,
  showInTabs = false,
  setOpenReport,
  isClosed,
  dateClosed,
  closedByUserRef,
  fillAvailableHeight = false,
}) => {
  const [undertakings, setUndertakings] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUndertaking, setSelectedUndertaking] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const excelExportRef = useRef(null);

  const undertakingRows = useMemo(
    () => (Array.isArray(undertakings) ? undertakings : asUndertakingsList(undertakings)),
    [undertakings],
  );

  const exportRows = useMemo(
    () =>
      undertakingRows.map((row) => ({
        date: row?.date ? DateFormat(row.date) : "",
        details: row?.details ?? "",
        amountDisplay: AmountFormat(row?.amount),
        dateDischarge: row?.dateDischarge ? DateFormat(row.dateDischarge) : "",
        isFinancial: row?.isFinancial ? "Yes" : "No",
      })),
    [undertakingRows],
  );

  const exportFileName = getLedgerExportFileName({
    tabLabel: "Undertakings",
    matterRef,
  });

  const exportGridToExcel = () => {
    if (!exportRows.length) {
      toast.info("No undertakings to export");
      return;
    }
    if (!excelExportRef.current) {
      toast.error("Export is not ready. Please try again.");
      return;
    }
    setIsExporting(true);
    try {
      excelExportRef.current.save(exportRows);
    } catch (error) {
      toast.error("Failed to export undertakings");
      setIsExporting(false);
      return;
    }
    setTimeout(() => setIsExporting(false), 500);
  };

  const fetchUndertakings = useCallback(async () => {
    if (!matterId) {
      setUndertakings([]);
      return;
    }
    setLoading(true);
    try {
      const response = await getMatterUndertakings(matterId);
      setUndertakings(asUndertakingsList(response.data));
    } catch (error) {
      console.error("Error fetching undertakings:", error);
      setUndertakings([]);
    } finally {
      setLoading(false);
    }
  }, [matterId]);

  useEffect(() => {
    fetchUndertakings();
  }, [matterId, fetchUndertakings, refreshData]);

  const handleAdd = (e) => {
    e.stopPropagation();
    setSelectedUndertaking(null);
    setShowAddModal(true);
  };

  const toolbarAction = showInTabs ? (
    <Button
      type="button"
      className="button-small bgBtnSecondary fw-bold"
      onClick={handleAdd}
      disabled={!matterId}
    >
      Add Undertaking
    </Button>
  ) : null;

  const toolbarHeaderCell = (props) =>
    ToolbarHeaderCell({
      ...props,
      matterId,
      isClosed,
      dateClosed,
      closedByUserRef,
      onPrint: () => {
        if (matterId) setOpenReport?.(true);
      },
      onShowPrevTrans: () => { },
      isExporting,
      exportReport: exportGridToExcel,
      showPreviousTransactions: false,
      toolbarAction,
    });

  const handleCloseModal = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setShowDeleteConfirm(false);
    setSelectedUndertaking(null);
  };

  const handleDelete = async () => {
    if (!selectedUndertaking?.undertakingId) return;
    setIsDeleting(true);
    try {
      await updateUndertaking(selectedUndertaking.undertakingId, {
        active: false,
      });
      toast.success("Undertaking deleted successfully");
      await fetchUndertakings();
      handleCloseModal();
    } catch (error) {
      console.error("Error deleting undertaking:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEdit = (undertaking) => {
    setSelectedUndertaking(undertaking);
    setShowEditModal(true);
  };

  const handleSubmit = async (values) => {
    try {
      if (!values.description || values.description.trim() === "") {
        toast.error("Details is required");
        return;
      }

      const undertakingData = {
        clientId: clientId || 0,
        matterId: matterId || null,
        details: values.description.trim(),
        amount: values.amount || 0,
        date: formatDateForAPI(values.date),
        dateDischarge:
          values.dateDischarge && formatDateForAPI(values.dateDischarge),
        isFinancial: values.financial || false,
        active: true,
      };

      if (selectedUndertaking) {
        await updateUndertaking(
          selectedUndertaking.undertakingId,
          undertakingData
        );
        toast.success("Undertaking updated successfully");
      } else {
        await addUndertaking(undertakingData);
        toast.success("Undertaking added successfully");
      }

      await fetchUndertakings();
      handleCloseModal();
    } catch (error) {
      console.error("Error saving undertaking:", error);
      throw error;
    }
  };

  const gridContent = (
    <>
      <ExcelExport
        data={exportRows}
        fileName={`${exportFileName}.xlsx`}
        ref={excelExportRef}
        onExportComplete={() => setIsExporting(false)}
      >
        <ExcelExportColumn field="date" title="Date" width={120} />
        <ExcelExportColumn field="details" title="Description" width={320} />
        <ExcelExportColumn field="amountDisplay" title="Amount" width={120} />
        <ExcelExportColumn
          field="dateDischarge"
          title="Date Discharge"
          width={140}
        />
        <ExcelExportColumn field="isFinancial" title="Financial" width={100} />
      </ExcelExport>
      <Grid
        scroll
        data={undertakings}
        className={`h-100 fs-md pb-1 ${fillAvailableHeight || !showInTabs
          ? ledgerStyles.grid_container_fill
          : ledgerStyles.grid_container
          } grid-fs13`}
        sortable
        resizable
        showLoader={loading}
        loader={
          <div
            style={{
              height: "500px",
            }}
          >
            <GridLoader />
          </div>
        }
      >
        <GridNoRecords>
          {loading ? (
            <span> </span>
          ) : (
            undertakings && undertakings.length === 0 && "No records available"
          )}
        </GridNoRecords>
        <GridColumn cells={{ headerCell: toolbarHeaderCell }}>
          <GridColumn
            field="date"
            title="Date"
            width="120px"
            cell={DateFormatCell}
            className="gridBoldField ps-4"
            headerClassName="ps-4"
          />
          <GridColumn field="details" title="Description" />
          <GridColumn
            field="amount"
            title="Amount"
            width="120px"
            cell={AmountFormatCell}
            headerClassName="text-end"
          />
          <GridColumn
            field="dateDischarge"
            title="Date Discharge"
            width="140px"
            cell={DateFormatCell}
          />
          <GridColumn
            field="isFinancial"
            title="Financial"
            width="100px"
            cell={(props) => (
              <td className="text-center">
                <Checkbox checked={!!props.dataItem.isFinancial} readOnly />
              </td>
            )}
          />
          <GridColumn
            title="Action"
            width="80px"
            cell={(props) => (
              <EditCommandCell {...props} enterEdit={handleEdit} />
            )}
          />
        </GridColumn>
      </Grid>
    </>
  );

  return (
    <div
      className={
        showInTabs ? "h-100 d-flex flex-column" : "container pt-3"
      }
    >
      {showInTabs ? (
        <div className="h-100 flex-grow-1" style={{ minHeight: 0 }}>
          {gridContent}
        </div>
      ) : (
        <Tile
          heading="Undertakings"
          className="mb-2 py-2 px-4 d-flex flex-column overflow-hidden dialog-tab-height"
          headingItems={
            <div>
              <Button
                type="button"
                className="button-small bgBtnSecondary fw-bold me-1"
                onClick={handleAdd}
              >
                Add Undertaking
              </Button>
            </div>
          }
        >
          <div className="flex-grow-1" style={{ minHeight: 0 }}>
            {gridContent}
          </div>
        </Tile>
      )}

      {showAddModal && (
        <Dialog title="Add Undertaking" onClose={handleCloseModal} width={500}>
          <UndertakingForm
            onCancel={handleCloseModal}
            onSubmit={handleSubmit}
            initialValues={{
              date: new Date(),
            }}
          />
        </Dialog>
      )}

      {showEditModal && (
        <Dialog title="Edit Undertaking" onClose={handleCloseModal} width={500}>
          <UndertakingForm
            onCancel={handleCloseModal}
            onSubmit={handleSubmit}
            onDelete={() => setShowDeleteConfirm(true)}
            initialValues={{
              description: selectedUndertaking?.details || "",
              amount: selectedUndertaking?.amount || 0,
              date: selectedUndertaking?.date
                ? new Date(selectedUndertaking.date)
                : null,
              dateDischarge: selectedUndertaking?.dateDischarge
                ? new Date(selectedUndertaking.dateDischarge)
                : null,
              financial: selectedUndertaking?.isFinancial || false,
            }}
          />
        </Dialog>
      )}

      {showDeleteConfirm && (
        <ConfirmationDialog
          content="Are you sure you want to delete this undertaking?"
          onClickYes={handleDelete}
          onClickNo={() => setShowDeleteConfirm(false)}
          onClose={() => setShowDeleteConfirm(false)}
          isLoading={isDeleting}
        />
      )}

    </div>
  );
};

export default UndertakingsList;
